import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '../../utils/cn';
import { type VariantProps } from 'class-variance-authority';
import {
  CHECKBOX_STATE,
  checkboxRootVariants,
  checkboxWrapperStyle,
  checkboxLabelContainerStyle,
  checkboxLabelStyle,
  checkboxLabelDestructiveStyle,
  checkboxSupportingTextStyle,
  checkboxSupportingTextDestructiveStyle,
  checkboxIconStyle,
  checkboxLabelWithIconStyle
} from './styles';
import { InfoTooltip, type InfoTooltipProps } from '../Tooltip/InfoTooltip';
import { TOOLTIP_ARROW, TOOLTIP_THEME } from '../Tooltip/styles';

export { CHECKBOX_STATE };

export interface CheckboxProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
      'color'
    >,
    VariantProps<typeof checkboxRootVariants> {
  /**
   * The primary label text for the checkbox
   */
  label?: string;
  /**
   * Supporting/helper text displayed below the label
   */
  supportingText?: string;
  /**
   * Info tooltip content to display next to the label
   */
  infoTooltip?: string;
  /**
   * Info tooltip props (theme, arrow, etc.)
   */
  infoTooltipProps?: Omit<InfoTooltipProps, 'content'>;
  /**
   * Data test id for testing
   */
  dataTestId?: string;
  /**
   * Custom className for the checkbox root
   */
  className?: string;
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      state = CHECKBOX_STATE.DEFAULT,
      label,
      supportingText,
      infoTooltip,
      infoTooltipProps,
      disabled,
      dataTestId,
      ...props
    },
    ref
  ) => {
    // Determine actual state considering disabled
    const actualState = disabled ? CHECKBOX_STATE.DISABLED : state;
    const isDestructive = state === CHECKBOX_STATE.DESTRUCTIVE;

    // If no label and no supporting text, render checkbox only
    if (!label && !supportingText) {
      return (
        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(
            checkboxRootVariants({ state: actualState }),
            'h-l w-l', // 16px x 16px
            className
          )}
          disabled={disabled}
          data-testid={dataTestId || 'checkbox'}
          {...props}>
          <CheckboxPrimitive.Indicator
            className={cn('flex items-center justify-center h-full w-full')}>
            {props.checked === 'indeterminate' ? (
              // Horizontal line icon from Figma - 12x12px container
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={checkboxIconStyle}>
                <path
                  d="M10.505 5.38C10.8502 5.38 11.13 5.65982 11.13 6.005C11.13 6.35018 10.8502 6.63 10.505 6.63H1.505C1.15982 6.63 0.88 6.35018 0.88 6.005C0.88 5.65982 1.15982 5.38 1.505 5.38H10.505Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              // Check solid icon from Figma - 12x12px container
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={checkboxIconStyle}>
                <path
                  d="M9.5528 2.81342C9.79114 2.56376 10.1869 2.55458 10.4366 2.79291C10.6863 3.03125 10.6954 3.42702 10.4571 3.6767L5.2071 9.1767C5.09092 9.2984 4.93051 9.36809 4.76228 9.37006C4.59401 9.37202 4.43205 9.30595 4.31306 9.18696L1.56306 6.43696C1.31898 6.19288 1.31898 5.79724 1.56306 5.55317C1.79189 5.32434 2.154 5.30996 2.39948 5.5102L2.44685 5.55317L4.74421 7.85053L9.5528 2.81342Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      );
    }

    // Render checkbox with label and/or supporting text
    const id = React.useId();

    return (
      <div className={cn(checkboxWrapperStyle, !label && 'items-center')}>
        <CheckboxPrimitive.Root
          ref={ref}
          id={id}
          className={cn(
            checkboxRootVariants({ state: actualState }),
            'h-l w-l',
            className
          )}
          disabled={disabled}
          data-testid={dataTestId || 'checkbox'}
          {...props}>
          <CheckboxPrimitive.Indicator
            className={cn('flex items-center justify-center h-full w-full')}>
            {props.checked === 'indeterminate' ? (
              // Horizontal line icon from Figma - 12x12px container
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={checkboxIconStyle}>
                <path
                  d="M10.505 5.38C10.8502 5.38 11.13 5.65982 11.13 6.005C11.13 6.35018 10.8502 6.63 10.505 6.63H1.505C1.15982 6.63 0.88 6.35018 0.88 6.005C0.88 5.65982 1.15982 5.38 1.505 5.38H10.505Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              // Check solid icon from Figma - 12x12px container
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={checkboxIconStyle}>
                <path
                  d="M9.5528 2.81342C9.79114 2.56376 10.1869 2.55458 10.4366 2.79291C10.6863 3.03125 10.6954 3.42702 10.4571 3.6767L5.2071 9.1767C5.09092 9.2984 4.93051 9.36809 4.76228 9.37006C4.59401 9.37202 4.43205 9.30595 4.31306 9.18696L1.56306 6.43696C1.31898 6.19288 1.31898 5.79724 1.56306 5.55317C1.79189 5.32434 2.154 5.30996 2.39948 5.5102L2.44685 5.55317L4.74421 7.85053L9.5528 2.81342Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        <div className={checkboxLabelContainerStyle}>
          {label && (
            <div className={checkboxLabelWithIconStyle}>
              <label 
                htmlFor={id} 
                className={cn(
                  isDestructive && !disabled
                    ? checkboxLabelDestructiveStyle
                    : checkboxLabelStyle
                )}>
                {label}
              </label>
              {infoTooltip && (
                <InfoTooltip
                  content={infoTooltip}
                  arrow={TOOLTIP_ARROW.TOP_CENTER}
                  theme={TOOLTIP_THEME.LIGHT}
                  {...infoTooltipProps}
                />
              )}
            </div>
          )}
          {supportingText && (
            <label
              htmlFor={id}
              className={cn(
                isDestructive && !disabled
                  ? checkboxSupportingTextDestructiveStyle
                  : checkboxSupportingTextStyle,
                !label && !isDestructive && !disabled && 'text-primary',
                !label && 'text-sm !leading-md'
              )}>
              {supportingText}
            </label>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
